import { EyeNoneIcon, EyeOpenIcon } from '@radix-ui/react-icons'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { useKestrel } from 'features/kestrelViewer'
import { useMarkupVisibility } from 'features/kestrelViewer/hooks/useMarkupVisibility'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import React, { FC } from 'react'
import styled from 'styled-components'

const StyledEyeClosedIcon = styled(EyeNoneIcon)`
  color: lightgray;
  &:hover {
    color: white;
  }
`

const StyledEyeOpenIcon = styled(EyeOpenIcon)`
  color: lightgray;
  &:hover {
    color: white;
  }
`

interface ToggleAllVisibilityButtonProps {
  type:
    | 'annotations'
    | 'measurements'
    | 'inspectionAreas'
    | 'historicalAnnotations'
    | 'primitives'
  count: number
}

export const ToggleAllVisibilityButton: FC<ToggleAllVisibilityButtonProps> = ({
  type,
  count,
}) => {
  const {
    annotations,
    historicalAnnotations,
    measurements,
    inspectionAreas,
    primitives,
    updateAnnotation,
    updateHistoricalAnnotations,
    updateMeasurement,
    updateInspectionArea,
    updatePrimitive,
    sendKVCommand,
  } = useKestrel()

  // Use separate hooks for each markup type
  const annotationsVisibility = useMarkupVisibility({
    items: annotations,
    updateFunction: (item, field, setter, value) =>
      updateAnnotation(item, field, setter, value as unknown),
    visibilityField: 'visible',
  })

  const historicalAnnotationsVisibility = useMarkupVisibility({
    items: historicalAnnotations,
    updateFunction: (item, field, setter, value) =>
      updateHistoricalAnnotations(item, field, setter, value as unknown),
    visibilityField: 'visible',
  })

  const measurementsVisibility = useMarkupVisibility({
    items: measurements,
    updateFunction: (item, field, setter, value) =>
      updateMeasurement(
        item,
        field,
        setter,
        value as string | boolean | (string | boolean)[]
      ),
    visibilityField: 'visible',
  })

  const inspectionAreasVisibility = useMarkupVisibility({
    items: inspectionAreas,
    updateFunction: (item, field, setter, value) =>
      updateInspectionArea(item, field, setter, value as unknown),
    visibilityField: 'visible',
  })

  const primitivesVisibility = useMarkupVisibility({
    items: primitives,
    updateFunction: (item, field, setter, value) =>
      updatePrimitive(item, field, setter, value as unknown),
    visibilityField: 'visible',
  })

  // Get the appropriate visibility state based on type
  const getVisibilityState = () => {
    switch (type) {
      case 'annotations':
        return annotationsVisibility
      case 'historicalAnnotations':
        return historicalAnnotationsVisibility
      case 'measurements':
        return measurementsVisibility
      case 'inspectionAreas':
        return inspectionAreasVisibility
      case 'primitives':
        return primitivesVisibility

      default:
        return { allVisible: true, toggleAll: () => {} }
    }
  }

  const { allVisible, toggleAll } = getVisibilityState()

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (!sendKVCommand) return

    const newVisibility = !allVisible
    const isHistorical = type === 'historicalAnnotations'

    let showAllCommand = KestrelCommands.SHOW_ALL
    let hideAllCommand = KestrelCommands.HIDE_ALL

    if (isHistorical) {
      showAllCommand = KestrelCommands.SHOW_ALL_HISTORICAL
      hideAllCommand = KestrelCommands.HIDE_ALL_HISTORICAL
    }

    // Send command to Unreal
    sendKVCommand({
      command: newVisibility ? showAllCommand : hideAllCommand,
      type: type,
      historical: isHistorical,
    })

    // Update local state
    toggleAll()
  }

  if (count === 0) return null

  const typeLabel =
    type === 'inspectionAreas'
      ? 'Inspection Areas'
      : type === 'primitives'
        ? 'Primitives'
        : type.charAt(0).toUpperCase() + type.slice(1)

  return (
    <TooltipWrapper
      label={`${allVisible ? 'Hide' : 'Show'} All ${typeLabel}`}
      side='top'>
      <div style={{ cursor: 'pointer' }} onClick={handleToggle}>
        {allVisible ? (
          <StyledEyeOpenIcon width={18} height={18} />
        ) : (
          <StyledEyeClosedIcon width={18} height={18} />
        )}
      </div>
    </TooltipWrapper>
  )
}
